import Right from 'assets/icons/Right';
import SAP from 'assets/icons/SAP';
import Workable from 'assets/icons/Workable';
import { Logo } from 'assets/logos';
import Button from 'common/components/Button';
import Chip from 'common/components/Chip';
import useNavigate from 'common/hooks/useNavigate';
import useTranslation from 'common/hooks/useTranslation';
import { formatDate } from 'common/utils';
import React from 'react';
import { useSelector } from 'react-redux';

import { selectCurrentCompany } from '../../common/slices/auth.slice';
import OfferSyncStatusBadge from '../components/OfferSyncStatusBadge';
import { findStatusState } from '../details/components/StatusCombo';

export const OfferTableItem = ({
  created_at,
  name,
  slug,
  status,
  externalSource,
  nSubmissions,
}) => {
  const navigate = useNavigate();

  const { t, i18n } = useTranslation('offers\\index');

  const externalOfferId = externalSource?.external_offer_id;

  const currentCompany = useSelector(selectCurrentCompany);
  const activeIntegration = currentCompany?.active_integration;

  const getSourceIcon = () => {
    switch (activeIntegration) {
      case 'SAPSF':
        return <SAP size='24' />;
      case 'WORKABLE':
        return <Workable size='24' />;
      default:
        return <Logo size='24' />;
    }
  };

  const getIconBorderColor = () => {
    switch (activeIntegration) {
      case 'WORKABLE':
        return 'soft-blue';
      case 'SAPSF':
        return 'soft-blue';
      default:
        return 'border-vibrant-orange';
    }
  };

  return (
    <tr className='h-[72px] border-b border-bright-grey'>
      <td className='whitespace-nowrap'>
        <div className='flex gap-3 items-center'>
          <div
            className={`flex items-center border ${getIconBorderColor()} rounded-[4px] p-[10px] h-[42px] w-[42px]`}
          >
            {getSourceIcon()}
          </div>
          <div className='flex flex-col items-start gap-1'>
            <div
              className={`flex items-center ${
                externalOfferId ? 'divide-x text-sonic-silver' : ''
              } `}
            >
              <span className='font-semibold text-raisin-black pr-2'>
                {name}
              </span>
              {externalOfferId && (
                <span className='text-sonic-silver pl-2 text-sm'>
                  {externalOfferId}
                </span>
              )}
            </div>
            <div
              className={`flex items-center ${
                nSubmissions ? 'divide-x text-sonic-silver' : ''
              }`}
            >
              {nSubmissions && (
                <span className='text-sonic-silver pr-2 text-sm'>
                  {t('participants', {
                    n_submissions: nSubmissions,
                  })}
                </span>
              )}
              <div
                className={`${
                  nSubmissions ? 'pl-2' : ''
                } inline-flex items-center gap-2 text-sm text-[#8D91A0]`}
              >
                <OfferSyncStatusBadge
                  externalSource={externalSource}
                  activeIntegration={activeIntegration}
                  chipVariantSufix={'light'}
                  i18n={i18n}
                  t={t}
                />
              </div>
            </div>
          </div>
        </div>
      </td>
      <td className='whitespace-nowrap'>
        <div className='flex flex-col items-start gap-2'>
          {status && (
            <>
              <Chip
                className='uppercase'
                variant={findStatusState(status)?.cssVariant}
              >
                {findStatusState(status)?.icon()}
                {t(`${findStatusState(status).id.toLowerCase()}`)}
              </Chip>
              <p className='text-sonic-silver text-sm'>
                {t('creation-on')} {formatDate(created_at, i18n, 'numeric')}
              </p>
            </>
          )}
        </div>
      </td>
      <td className='whitespace-nowrap' align='right'>
        <Button variant='secondary' onClick={() => navigate(`/offers/${slug}`)}>
          {t('see-offer')}
          <Right size='16px' color='#8D91A0' />
        </Button>
      </td>
    </tr>
  );
};
